import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import SolarPowerRoundedIcon from '@mui/icons-material/SolarPowerRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { DataGrid } from '@mui/x-data-grid';

import MonthRangePicker from '../../components/monthRangePicker';
import MultiSelectFilter from '../../components/multiSelectFilter';
import PageHeader from '../../components/pageHeader';
import { PageIntro } from '../../components/ui';
import {
    formatMonthParam,
    getCurrentMonthStart,
    getPreviousDayMonthStart
} from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatDecimal, formatPercentRatio, formatPercentage } from '../../utils/numberUtils';
import { isBlank } from '../../utils/stringFormat';
import '../css/realEstimatedBI.css';

const allOptionValue = 'all';

const kpiFilterOptions = [
    { value: allOptionValue, label: 'Todas' },
    { value: 'good', label: 'Acima de 100%' },
    { value: 'warning', label: 'Entre 95% e 100%' },
    { value: 'bad', label: 'Abaixo de 95%' },
    { value: 'nodata', label: 'Sem dado' }
];

const minMonth = new Date(2025, 0, 1);

const hasActiveSelection = (selectedValues) => !selectedValues.includes(allOptionValue);

const matchesSelection = (value, selectedValues) => (
    !hasActiveSelection(selectedValues) || selectedValues.includes(String(value))
);

const buildOptions = (rows, valueField, labelField, allLabel) => {
    const labelByValue = new Map();

    rows.forEach((row) => {
        if (isBlank(row[valueField])) return;

        labelByValue.set(String(row[valueField]), row[labelField]);
    });

    const options = [...labelByValue.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((left, right) => left.label.localeCompare(right.label));

    return [{ value: allOptionValue, label: allLabel }, ...options];
};

const getKpiRange = (kpi) => {
    if (kpi === null || kpi === undefined) {
        return 'nodata';
    }

    const kpiPercent = Number(kpi) * 100;

    if (kpiPercent < 95) {
        return 'bad';
    }

    if (kpiPercent < 100) {
        return 'warning';
    }

    return 'good';
};

const matchesKpiFilter = (kpi, selectedKpiRanges) => (
    !hasActiveSelection(selectedKpiRanges) || selectedKpiRanges.includes(getKpiRange(kpi))
);

const mapKpis = (kpis) => kpis.map((item) => ({
    id: item.usina_id,
    usina: item.usina,
    empresaId: item.empresa_id,
    empresa: item.empresa,
    siteId: item.site_id,
    site: item.site,
    potencia: item.potencia,
    gerado: item.gerado,
    estimado: item.estimado,
    kpi: item.kpi,
    cobertura: item.cobertura,
    inversorDiasSemFalha: item.inversor_dias_sem_falha
}));

const buildKpiGeral = (rows) => {
    const totals = rows.reduce((acc, item) => ({
        gerado: acc.gerado + Number(item.gerado),
        estimado: acc.estimado + Number(item.estimado)
    }), {
        gerado: 0,
        estimado: 0
    });

    return totals.estimado > 0 ? totals.gerado / totals.estimado : 0;
};

const RealEstimadoBI = () => {
    const [selectedEmpresas, setSelectedEmpresas] = useState([allOptionValue]);
    const [selectedSites, setSelectedSites] = useState([allOptionValue]);
    const [selectedKpiRanges, setSelectedKpiRanges] = useState([allOptionValue]);
    const [dataInicio, setDataInicio] = useState(getCurrentMonthStart);
    const [dataFim, setDataFim] = useState(getCurrentMonthStart);
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    const initialFetchStarted = useRef(false);

    const empresaOptions = useMemo(() => buildOptions(
        rows.filter((item) => matchesSelection(item.siteId, selectedSites)),
        'empresaId',
        'empresa',
        'Todas'
    ), [rows, selectedSites]);
    const siteOptions = useMemo(() => buildOptions(
        rows.filter((item) => matchesSelection(item.empresaId, selectedEmpresas)),
        'siteId',
        'site',
        'Todos'
    ), [rows, selectedEmpresas]);
    const scopedRows = useMemo(() => rows.filter((item) => (
        matchesSelection(item.empresaId, selectedEmpresas) &&
        matchesSelection(item.siteId, selectedSites)
    )), [rows, selectedEmpresas, selectedSites]);
    const filteredRows = useMemo(
        () => scopedRows.filter((item) => matchesKpiFilter(item.kpi, selectedKpiRanges)),
        [scopedRows, selectedKpiRanges]
    );
    const kpiGeral = useMemo(() => buildKpiGeral(scopedRows), [scopedRows]);

    const buscarRealEstimado = useCallback(async ({
        dataInicioValue,
        dataFimValue
    }) => {
        setIsLoading(true);

        const body = JSON.stringify({
            data_inicio: formatMonthParam(dataInicioValue),
            data_fim: formatMonthParam(dataFimValue)
        });

        const response = await fetchFlask('buscarRealEstimado', body);
        setRows(mapKpis(response.kpis));
        setIsLoading(false);
    }, []);

    useEffect(() => {
        async function fetchInitialData () {
            if (initialFetchStarted.current) {
                return;
            }

            initialFetchStarted.current = true;

            await buscarRealEstimado({
                dataInicioValue: dataInicio,
                dataFimValue: dataFim
            });

            setInitialDataLoaded(true);
        }

        fetchInitialData();
    }, []);

    useEffect(() => {
        if (!initialDataLoaded) return;

        buscarRealEstimado({
            dataInicioValue: dataInicio,
            dataFimValue: dataFim
        });
    }, [dataFim, dataInicio]);

    const handleDateRangeApply = (startDate, endDate) => {
        setDataInicio(startDate);
        setDataFim(endDate);
    };

    const kpiDistribution = useMemo(() => {
        const totalRows = scopedRows.length;
        const getPercentage = (range) => (
            totalRows
                ? (scopedRows.filter((item) => getKpiRange(item.kpi) === range).length / totalRows) * 100
                : 0
        );

        return {
            good: getPercentage('good'),
            warning: getPercentage('warning'),
            bad: getPercentage('bad')
        };
    }, [scopedRows]);

    const columns = useMemo(() => [
        {
            field: 'id',
            headerName: 'ID',
            type: 'number',
            width: 76,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'usina',
            headerName: 'Usina',
            flex: 1,
            minWidth: 260,
            renderCell: (params) => (
                <span className='real-estimated-bi-plant'>
                    <SolarPowerRoundedIcon />
                    {params.value}
                </span>
            )
        },
        {
            field: 'potencia',
            headerName: 'Potência (kWp)',
            type: 'number',
            width: 170,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => formatDecimal(params.value)
        },
        {
            field: 'kpi',
            headerName: 'KPI %',
            type: 'number',
            width: 132,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Box className='real-estimated-bi-kpi-cell'>
                    <span className={`real-estimated-bi-kpi is-${getKpiRange(params.value)}`}>
                        {getKpiRange(params.value) === 'nodata'
                            ? 'Sem dado'
                            : `${formatDecimal(Number(params.value) * 100)}%`}
                    </span>
                </Box>
            )
        },
        {
            field: 'cobertura',
            headerName: 'Cobertura de dados',
            type: 'number',
            width: 170,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Box className='real-estimated-bi-kpi-cell'>
                    <span className={`real-estimated-bi-kpi is-${Number(params.value) < 1 ? 'warning' : 'good'}`}>
                        {formatPercentRatio(params.value)}
                    </span>
                </Box>
            )
        },
        {
            field: 'inversorDiasSemFalha',
            headerName: 'Inversor-dias parados sem falha',
            type: 'number',
            width: 230,
            align: 'center',
            headerAlign: 'center'
        }
    ], []);

    return (
        <Box className='mainBox real-estimated-bi-page'>
            <PageHeader>KPI Real x Estimado</PageHeader>

            <PageIntro eyebrow='BI SS' title='KPI Real x Estimado'>
                Geração real contra a estimada por SPE e site, com a faixa de KPI de cada mês.
            </PageIntro>

            <Box className='real-estimated-bi-shell'>
                <Box className='real-estimated-bi-filters'>
                    <MonthRangePicker
                        startDate={dataInicio}
                        endDate={dataFim}
                        minDate={minMonth}
                        maxDate={getPreviousDayMonthStart()}
                        disabled={isLoading}
                        onApply={handleDateRangeApply}
                    />

                    <MultiSelectFilter
                        label='SPE'
                        labelId='real-estimado-empresa-filter'
                        options={empresaOptions}
                        value={selectedEmpresas}
                        disabled={isLoading}
                        onChange={setSelectedEmpresas}
                    />

                    <MultiSelectFilter
                        label='Site'
                        labelId='real-estimado-site-filter'
                        options={siteOptions}
                        value={selectedSites}
                        disabled={isLoading}
                        onChange={setSelectedSites}
                    />

                    <MultiSelectFilter
                        label='Faixa KPI'
                        labelId='real-estimado-kpi-filter'
                        options={kpiFilterOptions}
                        value={selectedKpiRanges}
                        disabled={isLoading}
                        onChange={setSelectedKpiRanges}
                    />
                </Box>

                <Box className='real-estimated-bi-summary real-estimated-bi-summary-with-average'>
                    <Box className={`is-${getKpiRange(kpiGeral)}`}>
                        <span><SpeedRoundedIcon /> KPI geral</span>
                        <strong>{formatPercentRatio(kpiGeral)}</strong>
                    </Box>
                    <Box className='is-good'>
                        <span><CheckCircleRoundedIcon /> Usinas acima de 100%</span>
                        <strong>{formatPercentage(kpiDistribution.good)}%</strong>
                    </Box>
                    <Box className='is-warning'>
                        <span><WarningAmberRoundedIcon /> Usinas entre 95% e 100%</span>
                        <strong>{formatPercentage(kpiDistribution.warning)}%</strong>
                    </Box>
                    <Box className='is-bad'>
                        <span><ReportProblemRoundedIcon /> Usinas abaixo de 95%</span>
                        <strong>{formatPercentage(kpiDistribution.bad)}%</strong>
                    </Box>
                    <Box>
                        <span><QueryStatsRoundedIcon /> Usinas exibidas</span>
                        <strong>{filteredRows.length}</strong>
                    </Box>
                </Box>

                <Box className='real-estimated-bi-table-panel'>
                    <DataGrid
                        className='real-estimated-bi-grid'
                        rows={filteredRows}
                        columns={columns}
                        loading={isLoading}
                        disableRowSelectionOnClick
                        pageSizeOptions={[100, 150]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 100
                                }
                            }
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default RealEstimadoBI;
